import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, Loader2, Timer } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { getPublicGenieById } from "@/data/discovery/genie-catalog";
import { getCapability } from "@/data/genies/_capabilities";
import { getBrandForPersona } from "@/data/genies/persona-brands";
import { useDemoConversation } from "@/hooks/demo/useDemoConversation";
import { captureDiscoverAttribution } from "@/lib/discover-attribution";
import { trackEvent, trackEventOnce } from "@/lib/ga4";
import { Agent } from "@/types/agent/agent";
import type { PublicGeniePersona } from "@/types/discovery-catalog";
import { DEFAULT_BRANDING, GenieBranding } from "@/types/ui/branding";
import LiveAgentPage from "../agent-interfaces/LiveAgentPage";
import { CloneGenieOverlay } from "./components/CloneGenieOverlay";
import { DemoHeroPanel } from "./components/DemoHeroPanel";
import { PostConversationCard } from "./components/PostConversationCard";
import { YourBrandHereBadge } from "./components/YourBrandHereBadge";

const DEMO_DURATION_SECONDS = 600; // 10 minutes

const DemoPage = () => {
  const { personaId } = useParams<{ personaId: string }>();
  const navigate = useNavigate();

  // Persona & brand data
  const persona = getPublicGenieById(personaId || "");
  const brand = getBrandForPersona(personaId || "");
  const attribution = useMemo(() => captureDiscoverAttribution(), []);
  const eventContext = useMemo(
    () => ({
      persona_id: persona?.id || personaId || "",
      industry: persona?.industryId || "",
      business_type: persona?.subcategoryId || "",
      capability: persona?.capabilityType || "",
      ...attribution,
    }),
    [attribution, persona, personaId],
  );

  // Demo provisioning — eagerly provision on mount
  const { startDemo, isProvisioning, provisionError, demoAgent } = useDemoConversation();
  const [elevenlabsAgentId, setElevenlabsAgentId] = useState<string | null>(null);

  // Clone sheet
  const [clonePersona, setClonePersona] = useState<PublicGeniePersona | null>(null);

  // Timer
  const [timeRemaining, setTimeRemaining] = useState(DEMO_DURATION_SECONDS);
  const [timerActive, setTimerActive] = useState(false);

  // Conversation phase
  const [conversationPhase, setConversationPhase] = useState<"pre" | "active" | "post">("pre");
  const conversationStartedAtRef = useRef<number | null>(null);

  // Responsive
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Build branding from persona brand data
  const primaryColor = brand?.primaryColor || "#4E9CFF";
  const safeBrandName = brand?.brandName.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  const logoPath = safeBrandName ? `/brand-logos/${safeBrandName}-stacked.png` : null;

  const capability = getCapability(persona?.capabilityType || "");

  // Provision eagerly on mount
  useEffect(() => {
    if (personaId && !elevenlabsAgentId && !isProvisioning) {
      startDemo(personaId)
        .then((id) => setElevenlabsAgentId(id))
        .catch(() => {});
    }
  }, [personaId]);

  // Responsive layout
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Timer countdown
  useEffect(() => {
    if (!timerActive || timeRemaining <= 0) return;
    const interval = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          setTimerActive(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [timerActive, timeRemaining]);

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  // Build the Agent object LiveAgentPage expects. The demo agent row carries the
  // ElevenLabs id (`elevenlabs_id`) and the joined `agent_pages` row — but the
  // page row alone has no `elevenlabs_id`, so passing it directly leaves the
  // voice hook without an agent id and every "Start Conversation" fails before
  // it even requests a signed URL. Reshape it into a proper Agent (elevenlabs_id
  // + nested `page`) and only expose it once the ElevenLabs agent is ready.
  const agentPageData: Agent | null = useMemo(() => {
    if (!demoAgent?.elevenlabs_id) return null;

    // DEPRECATED: agent_pages table is obsolete and scheduled for deletion — do not add new usages.
    const page = Array.isArray(demoAgent.agent_pages)
      ? (demoAgent.agent_pages[0] as any)
      : (demoAgent.agent_pages as any);

    const pageBranding = page?.branding as GenieBranding | undefined;
    const agentBranding = demoAgent.branding as GenieBranding | undefined;
    const pageHasLogo = pageBranding?.logoUrl || pageBranding?.customAvatarUrl;
    const agentHasLogo = agentBranding?.logoUrl || agentBranding?.customAvatarUrl;

    // Build fallback branding from persona brand registry (local logo path)
    const fallbackBranding: Partial<GenieBranding> = brand
      ? {
          primaryColor: brand.primaryColor,
          secondaryColor: brand.secondaryColor,
          brandName: brand.brandName,
          tagline: brand.tagline,
          logoUrl: logoPath ?? undefined,
        }
      : {};

    const mergedBranding: GenieBranding = {
      ...DEFAULT_BRANDING,
      ...fallbackBranding,
      ...(pageHasLogo ? pageBranding : {}),
      ...(agentHasLogo ? agentBranding : {}),
      // Ensure logoUrl is set — prefer the genie's own branding, then the old page copy, then the local path
      logoUrl: agentBranding?.logoUrl || pageBranding?.logoUrl || logoPath || DEFAULT_BRANDING.logoUrl,
    };

    return {
      ...demoAgent,
      branding: mergedBranding,
      welcome_message: demoAgent.welcome_message || page?.welcome_message || null,
      url_name: demoAgent.url_name || page?.url_name || null,
      page: {
        id: page?.id ?? null,
        agent_id: demoAgent.id ?? null,
        el_agent_id: demoAgent.elevenlabs_id ?? page?.elevenlabs_agent_id ?? null,
        url_name: page?.url_name ?? demoAgent.url_name ?? null,
        welcome_message: page?.welcome_message ?? demoAgent.welcome_message ?? null,
        target_audience: page?.audience_type ?? null,
        genie_image_url: mergedBranding.logoUrl ?? page?.genie_image_url ?? null,
        branding: mergedBranding,
        metadata: null,
        sandbox_link: null,
        info_links: page?.info_links ?? null,
        created_at: page?.created_at ?? null,
        updated_at: page?.updated_at ?? null,
      },
    } as Agent;
  }, [demoAgent, brand, logoPath]);

  useEffect(() => {
    if (!agentPageData || !personaId) return;
    trackEventOnce(`demo_ready_${personaId}`, "demo_ready", eventContext);
  }, [agentPageData, eventContext, personaId]);

  // Stable demo mode callbacks — must be before any early returns (Rules of Hooks)
  const handleDemoConversationStart = useCallback(() => {
    conversationStartedAtRef.current = Date.now();
    trackEvent("conversation_start", eventContext);
    setConversationPhase("active");
    setTimerActive(true);
    setTimeRemaining(DEMO_DURATION_SECONDS);
    // Fires once per persona per session — the "they actually talked to it" signal.
    trackEventOnce(`demo_engaged_${personaId}`, "discover_demo_engaged", { persona_id: personaId });
  }, [eventContext, personaId]);

  const handleDemoConversationEnd = useCallback(() => {
    const durationSeconds = conversationStartedAtRef.current
      ? Math.round((Date.now() - conversationStartedAtRef.current) / 1000)
      : undefined;
    trackEvent("conversation_end", { ...eventContext, duration_seconds: durationSeconds });
    conversationStartedAtRef.current = null;
    setConversationPhase("post");
    setTimerActive(false);
  }, [eventContext]);

  const handleCloneClick = useCallback(() => {
    if (!persona) return;
    trackEvent("clone_opened", eventContext);
    setClonePersona(persona);
  }, [eventContext, persona]);

  const demoModeConfig = useMemo(
    () => ({
      maxDurationSeconds: DEMO_DURATION_SECONDS,
      onConversationStart: handleDemoConversationStart,
      onConversationEnd: handleDemoConversationEnd,
    }),
    [handleDemoConversationStart, handleDemoConversationEnd],
  );

  // Not found
  if (!persona) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#F9FAFC",
        }}
      >
        <div className="text-center space-y-4">
          <p style={{ color: "#666", fontSize: "1.25rem" }}>Genie not found</p>
          <Button variant="outline" onClick={() => navigate("/discover")}>
            Back to Discover
          </Button>
        </div>
      </div>
    );
  }

  // Provisioning / loading screen — branded gradient
  if (!agentPageData) {
    const secondaryColor = brand?.secondaryColor || primaryColor;
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
          gap: "16px",
        }}
      >
        <button
          onClick={() => navigate(-1)}
          style={{
            position: "absolute",
            top: "20px",
            left: "20px",
            background: "rgba(255, 255, 255, 0.15)",
            border: "1px solid rgba(255, 255, 255, 0.25)",
            borderRadius: "50%",
            width: "36px",
            height: "36px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "white",
            cursor: "pointer",
            zIndex: 100,
            backdropFilter: "blur(4px)",
          }}
        >
          <ArrowLeft size={16} />
        </button>

        {provisionError ? (
          <div className="text-center space-y-4">
            <p style={{ color: "rgba(255,255,255,0.9)", fontSize: "1rem", fontWeight: 600 }}>
              Failed to set up demo
            </p>
            <p style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.85rem" }}>{provisionError}</p>
            <Button
              variant="outline"
              onClick={() => navigate("/discover")}
              style={{ color: "white", borderColor: "rgba(255,255,255,0.3)" }}
            >
              Back to Discover
            </Button>
          </div>
        ) : (
          <>
            <Loader2 className="animate-spin" size={32} style={{ color: "white" }} />
            <p style={{ color: "rgba(255,255,255,0.85)", fontSize: "0.95rem", fontWeight: 500 }}>
              Setting up {persona.name}...
            </p>
          </>
        )}
      </div>
    );
  }

  // Handle "Try Again" after demo ends
  const handleTryAgain = () => {
    setTimeRemaining(DEMO_DURATION_SECONDS);
    setConversationPhase("pre");
  };

  const showHeroPanel = conversationPhase === "pre" && !isMobile;

  return (
    <div
      style={{
        height: "100vh",
        width: "100%",
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        background: "#F9FAFC",
        overflow: "hidden",
      }}
    >
      {/* LEFT: Branded hero panel — desktop only, pre-conversation */}
      <AnimatePresence>
        {showHeroPanel && (
          <motion.div
            key="hero-panel"
            initial={{ width: "42%", opacity: 1 }}
            exit={{ width: 0, opacity: 0, overflow: "hidden" }}
            transition={{ duration: 0.45, ease: "easeInOut" }}
            style={{
              height: "100%",
              flexShrink: 0,
              minWidth: 0,
            }}
          >
            <DemoHeroPanel
              persona={persona}
              brand={brand}
              primaryColor={primaryColor}
              logoPath={logoPath}
              capability={capability}
              isMobile={false}
              onBack={() => navigate(-1)}
              onCloneClick={handleCloneClick}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* RIGHT: Conversation area */}
      <motion.div
        layout
        transition={{ duration: 0.45, ease: "easeInOut" }}
        style={{
          flex: 1,
          minWidth: 0,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          position: "relative",
        }}
      >
        {/* Mobile: hero panel above conversation in pre-state */}
        {conversationPhase === "pre" && isMobile && (
          <DemoHeroPanel
            persona={persona}
            brand={brand}
            primaryColor={primaryColor}
            logoPath={logoPath}
            capability={capability}
            isMobile
            onBack={() => navigate(-1)}
            onCloneClick={handleCloneClick}
          />
        )}

        {/* Top bar — only for active/post states (desktop), or active/post on mobile */}
        {conversationPhase !== "pre" && (
          <div
            style={{
              padding: "8px 16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: "1px solid #E5E7EB",
              background: "white",
              zIndex: 50,
              flexShrink: 0,
            }}
          >
            <button
              onClick={() => navigate(-1)}
              style={{
                background: "transparent",
                border: "1px solid #E5E7EB",
                borderRadius: "50%",
                width: "32px",
                height: "32px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#6B7280",
                cursor: "pointer",
                flexShrink: 0,
                transition: "all 0.2s ease",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = "#F3F4F6";
                e.currentTarget.style.color = "#374151";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.color = "#6B7280";
              }}
            >
              <ArrowLeft size={16} strokeWidth={2} />
            </button>

            {timerActive && (
              <Badge className="bg-primary/10 text-primary text-xs tabular-nums px-3 py-1">
                <Timer className="w-3 h-3 mr-1" />
                {formatTime(timeRemaining)}
              </Badge>
            )}

            {!timerActive && <div style={{ width: 32, flexShrink: 0 }} />}
          </div>
        )}

        {/* "Your Brand Here" floating badge — visible only pre-conversation */}
        <AnimatePresence>
          {conversationPhase === "pre" && <YourBrandHereBadge isMobile={isMobile} />}
        </AnimatePresence>

        {/* Main content */}
        <div style={{ flex: 1, minHeight: 0, position: "relative" }}>
          {conversationPhase === "post" ? (
            <div
              style={{
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "24px",
                background: `linear-gradient(160deg, ${primaryColor}08 0%, #F9FAFC 40%, ${brand?.secondaryColor || primaryColor}06 100%)`,
              }}
            >
              <PostConversationCard
                persona={persona}
                brand={brand}
                primaryColor={primaryColor}
                logoPath={logoPath}
                capability={capability}
                isMobile={isMobile}
                onCloneClick={handleCloneClick}
                onTryAgain={handleTryAgain}
              />
            </div>
          ) : (
            <LiveAgentPage agentPageData={agentPageData} portalMode demoMode={demoModeConfig} />
          )}
        </div>
      </motion.div>

      {/* Clone Overlay */}
      {clonePersona && (
        <CloneGenieOverlay
          open={!!clonePersona}
          onOpenChange={(open) => {
            if (!open) setClonePersona(null);
          }}
          persona={clonePersona}
        />
      )}
    </div>
  );
};

export default DemoPage;
